type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogPayload {
  [key: string]: unknown
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

const isDev = import.meta.env.DEV

function currentLevel(): number {
  const envLevel = (import.meta.env.VITE_LOG_LEVEL as LogLevel) ?? 'debug'
  return LOG_LEVELS[envLevel] ?? LOG_LEVELS.debug
}

function normalizeError(payload?: LogPayload): LogPayload | undefined {
  if (!payload) return undefined

  const result = { ...payload }

  if (result.err instanceof Error) {
    result.err = {
      name: result.err.name,
      message: result.err.message,
      stack: result.err.stack,
      ...(result.err as unknown as Record<string, unknown>),
    }
  }

  return result
}

function writeToConsole(
  level: LogLevel,
  message: string,
  payload?: LogPayload,
) {
  const timestamp = new Date().toISOString()
  const prefix = `[${timestamp}] [${level.toUpperCase()}]`
  const args: unknown[] = [`${prefix} ${message}`]
  if (payload) args.push(payload)

  switch (level) {
    case 'debug':
      console.debug(...args)
      break
    case 'info':
      console.info(...args)
      break
    case 'warn':
      console.warn(...args)
      break
    case 'error':
      console.error(...args)
      break
  }
}

/**
 * Application logger.
 *
 * - DEV: writes structured messages to the browser console.
 * - PROD: silent by default. When a monitoring service (Sentry, Datadog, etc.)
 *   is configured, forward `warn` and `error` entries to it by extending the
 *   `reportToMonitoring` function below.
 */
export const logger = {
  debug(message: string, payload?: LogPayload) {
    if (isDev && LOG_LEVELS.debug >= currentLevel()) {
      writeToConsole('debug', message, normalizeError(payload))
    }
  },

  info(message: string, payload?: LogPayload) {
    if (isDev && LOG_LEVELS.info >= currentLevel()) {
      writeToConsole('info', message, normalizeError(payload))
    }
  },

  warn(message: string, payload?: LogPayload) {
    if (isDev && LOG_LEVELS.warn >= currentLevel()) {
      writeToConsole('warn', message, normalizeError(payload))
    }
    reportToMonitoring('warn', message, payload)
  },

  error(message: string, payload?: LogPayload) {
    if (isDev && LOG_LEVELS.error >= currentLevel()) {
      writeToConsole('error', message, normalizeError(payload))
    }
    reportToMonitoring('error', message, payload)
  },
}

/**
 * Placeholder for external monitoring integration.
 * Replace the body with e.g. Sentry.captureMessage / Sentry.captureException
 * when a monitoring provider is adopted.
 */
function reportToMonitoring(
  _level: LogLevel,
  _message: string,
  _payload?: LogPayload,
) {
  // Example Sentry integration:
  // if (_level === 'error' && _payload?.err instanceof Error) {
  //   Sentry.captureException(_payload.err, { extra: { message: _message, ..._payload } })
  // } else {
  //   Sentry.captureMessage(_message, _level)
  // }
}
